import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { TOP_ATTRACTIONS_SKELETON_COUNT } from 'constants/experience'
import Places from 'constants/places'
import useTopAttractions from 'hooks/Experiences/useTopAttractions'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { fillArray } from 'lib/array/arrayUtils'

const LE_CDN_PATTERN = /^https?:\/\/(?:test-)?images\.luxuryescapes\.com\/([^/.]+)/

/**
 * Convert an attraction imageUrl to an App.Image object.
 * LE CDN URLs are converted to { id } so Cloudinary transforms (crop, resize,
 * quality) apply consistently between admin and customer views.
 * External URLs (Wikipedia, etc.) pass through as { url }.
 */
export function toAttractionImage(imageUrl: string): App.Image {
  const match = imageUrl.match(LE_CDN_PATTERN)
  if (match) return { id: match[1] }
  if (imageUrl.startsWith('http')) return { url: imageUrl }
  return { id: imageUrl }
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding-top: ${rem(12)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(16)};
    gap: ${rem(20)};
  }
`

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: 0;
  background: none;
  text-align: left;
  transition: border-color 0.2s ease;
  overflow: hidden;

  &.selected {
    border-color: ${(props) => props.theme.palette.brand.primary.normal};
  }
`

const TileImage = styled.div`
  width: ${rem(220)};
  height: ${rem(80)};
  overflow: hidden;
  background-color: ${(props) => props.theme.palette.neutral.default.seven};
`

const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(4)};
  width: ${rem(220)};
`

const SkeletonImage = styled(LoadingBox)`
  width: ${rem(220)};
  height: ${rem(80)};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const SkeletonText = styled(LoadingBox)`
  width: ${rem(160)};
  height: ${rem(16)};
  border-radius: ${(props) => props.theme.borderRadius.XS};
`

const SkeletonCaption = styled(LoadingBox)`
  width: ${rem(200)};
  height: ${rem(12)};
  border-radius: ${(props) => props.theme.borderRadius.XS};
  margin-top: ${rem(4)};
`

interface Props {
  place?: App.Place;
  selectedAttractionId?: string;
  onAttractionSelect: (attractionId: string | undefined) => void;
}

function ExperienceSearchTopAttractions({
  place,
  selectedAttractionId,
  onAttractionSelect,
}: Props) {
  const intl = useIntl()
  const { attractions, fetching } = useTopAttractions(place)

  const handleAttractionClick = useCallback(
    (attractionId: string) => {
      onAttractionSelect(
        selectedAttractionId === attractionId ? undefined : attractionId,
      )
    },
    [selectedAttractionId, onAttractionSelect],
  )

  const isAnywhere = place?.id === Places.Anywhere.id
  const title = isAnywhere
    ? intl.formatMessage({
        id: 'U_OG6MPWTUNM2_UJAQe3',
        defaultMessage: 'Explore top attractions',
        description:
          'Title for top attractions section when no specific destination is selected',
      })
    : intl.formatMessage(
        {
          id: 'LNY3NSyR57ioxICxuOV3',
          defaultMessage: 'Explore top attractions in {placeName}',
          description:
            'Title for top attractions section in experience search page',
        },
        { placeName: place?.name },
      )

  if (!place || (!fetching && attractions.length === 0)) {
    return null
  }

  return (
    <Root>
      <BodyText variant="medium">{title}</BodyText>
      <CardCarousel pageSize="auto" snap="start" gap={12} width={rem(220)}>
        {fetching && attractions.length === 0 &&
          fillArray(TOP_ATTRACTIONS_SKELETON_COUNT).map((id) => (
            <div key={id}>
              <SkeletonImage />
              <TileContent>
                <SkeletonText />
                <CSSBreakpoint min="desktop">
                  <SkeletonCaption />
                </CSSBreakpoint>
              </TileContent>
            </div>
          ))}
        {attractions.length > 0 &&
          attractions.map((attraction) => (
            <Tile
              key={attraction.attractionId}
              className={clsx({
                selected: selectedAttractionId === attraction.attractionId,
              })}
              onClick={() => handleAttractionClick(attraction.attractionId)}
              aria-pressed={selectedAttractionId === attraction.attractionId}
            >
              <TileImage>
                {attraction.imageUrl && (
                  <Image
                    image={toAttractionImage(attraction.imageUrl)}
                    width={220}
                    height={80}
                    aspectRatio="11:4"
                    quality="good"
                    alt={attraction.name}
                    loading="lazy"
                    fit="center"
                  />
                )}
              </TileImage>
              <TileContent>
                <BodyText variant="medium" weight="bold" lineClamp={2}>
                  {attraction.name}
                </BodyText>
                <CSSBreakpoint min="desktop">
                  {attraction.description && (
                    <Caption
                      variant="medium"
                      lineClamp={2}
                      colour="neutral-three"
                    >
                      {attraction.description}
                    </Caption>
                  )}
                </CSSBreakpoint>
              </TileContent>
            </Tile>
          ))}
      </CardCarousel>
    </Root>
  )
}

export default ExperienceSearchTopAttractions
